import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Check, Users, ClipboardCheck } from "lucide-react";
import useSeo from "@/lib/useSeo";

export default function MemberHub() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const nav = useNavigate();

  useSeo({
    title: "Your board hub — The Value Realisation Index",
    description: "Take the Index and invite your direct reports to unlock the Leadership Cascade.",
    path: `/team/${token}`,
    noindex: true,
  });

  useEffect(() => {
    api.get(`/board-members/by-own-token/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(e => setError(e.response?.data?.detail || "Link not recognised"));
  }, [token]);

  if (error) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-3xl mx-auto p-12 text-heritage" data-testid="hub-error">{error}</div><Footer /></div>;
  if (!info) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-3xl mx-auto p-12 tick-label">Loading…</div></div>;

  const teamUrl = `${window.location.origin}/team-invite/${info.report_token}`;
  const copy = async () => {
    await navigator.clipboard.writeText(teamUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="tick-label-accent mb-6">Your Private Hub</div>
          <h1 className="display-serif text-5xl md:text-7xl leading-tight">
            <span className="text-oxford">{info.name}</span>
            <span className="block text-orange italic text-3xl md:text-5xl mt-3">for {info.organisation}</span>
          </h1>
          <div className="rule-orange my-8" />
          <p className="max-w-2xl text-lg text-charcoal leading-relaxed">
            Two things to do here. First, complete the Value Realisation Index yourself — twenty minutes, twenty-five questions. Second, forward the team invitation below to your direct reports. Every response they submit strengthens the Leadership Cascade for {info.organisation}.
          </p>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-oxford/10">
        {/* Own diagnostic */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }}
          className="bg-linen p-10"
        >
          <div className="tick-label-accent mb-3"><ClipboardCheck className="inline w-3.5 h-3.5 mr-2" strokeWidth={1.5}/>Step 01</div>
          <h2 className="display-serif text-3xl">Take the Index yourself.</h2>
          <p className="mt-4 text-charcoal">
            Twenty minutes. Answer honestly and quickly — first-instinct answers are the most accurate. Your individual VRI will be visible only to the facilitator and to you.
          </p>
          {info.already_completed ? (
            <div className="mt-8 flex items-center gap-3">
              <div className="tick-label-accent">✓ Completed</div>
              <Link to={`/results/${info.own_submission_id}`} data-testid="hub-view-own-result" className="btn-ghost">View your result <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/></Link>
            </div>
          ) : (
            <button data-testid="hub-take-index" onClick={() => nav(`/diagnostic?member=${token}`)} className="btn-orange mt-8">
              Begin the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          )}
        </motion.div>

        {/* Team invite */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }}
          className="bg-linen p-10"
        >
          <div className="tick-label-accent mb-3"><Users className="inline w-3.5 h-3.5 mr-2" strokeWidth={1.5}/>Step 02</div>
          <h2 className="display-serif text-3xl">Invite your direct reports.</h2>
          <p className="mt-4 text-charcoal">
            Forward the link below to every person who reports directly to you. Their responses form your <em>Leadership Cascade</em> — a measure of how tightly your team's view of reality tracks with yours.
          </p>
          <div className="mt-8">
            <label className="tick-label block mb-2">Team invitation URL</label>
            <div className="flex gap-2">
              <input data-testid="hub-team-url" readOnly value={teamUrl} className="flex-1 border border-oxford/20 bg-linen px-3 py-3 font-mono text-xs" />
              <button data-testid="hub-copy-team" onClick={copy} className="btn-ghost px-3">
                {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
              </button>
            </div>
            <p className="mt-3 text-xs text-charcoal">Anyone with this link can submit a response linked to you. They will see your name so they know whose team they are joining.</p>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
